// ItemLinesTab.js - Item lines (appearance templates) configuration
import {
  React,
  useEffect,
  useState,
  Box,
  Button,
  CircularProgress,
  Stack,
  DataGrid,
  authFetch,
  authFetchRequest,
  confirmC
} from './AppearanceCommon';

import NewItemLineDialog from './NewItemLineDialog';
import EditItemLineDialog from './EditItemLineDialog';
import { useProductScope } from '../utils/productScope';

const ItemLinesTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openNewDialog, setOpenNewDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editRowData, setEditRowData] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [filterModel, setFilterModel] = useState({ items: [] });
  const { canSeeScope } = useProductScope();

  const fetchData = () => {
    setLoading(true);
    authFetch('/appearance-templates/')
      .then(data => { setRows(data); setLoading(false); })
      .catch(err => { console.error('Failed to fetch:', err); setLoading(false); });
  };

  useEffect(() => { fetchData(); }, []);

  const handleOpenEditDialog = () => {
    if (!selectedRow) return;
    setEditRowData(selectedRow);
    setOpenEditDialog(true);
  };

  const handleDelete = async () => {
    if (!selectedRow) return;
    const confirmed = await confirmC('Are you sure you want to delete this item line?');
    if (!confirmed) return;
    try {
      await authFetchRequest(`/appearance-templates/${selectedRow.id}`, null, 'DELETE');
      setRows(prevRows => prevRows.filter(row => row.id !== selectedRow.id));
      setSelectedRow(null);
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const handleNewClose = (newRow) => {
    setOpenNewDialog(false);
    if (newRow && newRow.id) {
      setRows(prevRows => [...prevRows, newRow]);
    }
  };

  const handleEditSave = (updatedRow) => {
    if (updatedRow && updatedRow.id) {
      setRows(prevRows => prevRows.map(row => row.id === updatedRow.id ? updatedRow : row));
      if (selectedRow?.id === updatedRow.id) setSelectedRow(updatedRow);
    }
    setOpenEditDialog(false);
    setEditRowData(null);
  };

  const handleEditClose = () => {
    setOpenEditDialog(false);
    setEditRowData(null);
  };

  // Move row up (decrease order_place)
  const handleMoveUp = async () => {
    if (!selectedRow) return;

    // Sort rows by order_place to find the previous row
    const sortedRows = [...rows].sort((a, b) => (a.order_place || 0) - (b.order_place || 0));
    const currentIndex = sortedRows.findIndex(r => r.id === selectedRow.id);

    if (currentIndex <= 0) return; // Already at top

    const prevRow = sortedRows[currentIndex - 1];
    const currentOrder = selectedRow.order_place || 0;
    const prevOrder = prevRow.order_place || 0;

    // If same order, decrement current by 1
    const newCurrentOrder = currentOrder === prevOrder ? prevOrder - 1 : prevOrder;
    const newPrevOrder = currentOrder;

    try {
      // Swap order_place values
      await authFetchRequest(`/appearance-templates/${selectedRow.id}`, { order_place: newCurrentOrder }, 'PUT');
      await authFetchRequest(`/appearance-templates/${prevRow.id}`, { order_place: newPrevOrder }, 'PUT');

      // Update local state
      setRows(prevRows => prevRows.map(row => {
        if (row.id === selectedRow.id) return { ...row, order_place: newCurrentOrder };
        if (row.id === prevRow.id) return { ...row, order_place: newPrevOrder };
        return row;
      }));
      setSelectedRow({ ...selectedRow, order_place: newCurrentOrder });
    } catch (err) {
      console.error('Move up failed:', err);
    }
  };

  // Move row down (increase order_place)
  const handleMoveDown = async () => {
    if (!selectedRow) return;

    // Sort rows by order_place to find the next row
    const sortedRows = [...rows].sort((a, b) => (a.order_place || 0) - (b.order_place || 0));
    const currentIndex = sortedRows.findIndex(r => r.id === selectedRow.id);

    if (currentIndex >= sortedRows.length - 1) return; // Already at bottom

    const nextRow = sortedRows[currentIndex + 1];
    const currentOrder = selectedRow.order_place || 0;
    const nextOrder = nextRow.order_place || 0;

    // If same order, increment current by 1
    const newCurrentOrder = currentOrder === nextOrder ? nextOrder + 1 : nextOrder;
    const newNextOrder = currentOrder;

    try {
      // Swap order_place values
      await authFetchRequest(`/appearance-templates/${selectedRow.id}`, { order_place: newCurrentOrder }, 'PUT');
      await authFetchRequest(`/appearance-templates/${nextRow.id}`, { order_place: newNextOrder }, 'PUT');

      // Update local state
      setRows(prevRows => prevRows.map(row => {
        if (row.id === selectedRow.id) return { ...row, order_place: newCurrentOrder };
        if (row.id === nextRow.id) return { ...row, order_place: newNextOrder };
        return row;
      }));
      setSelectedRow({ ...selectedRow, order_place: newCurrentOrder });
    } catch (err) {
      console.error('Move down failed:', err);
    }
  };

  const columns = [
    { field: 'id', headerName: 'Item', width: 150 },
    { field: 'product_type', headerName: 'Product', width: 100 },
    { field: 'label', headerName: 'Label', width: 150 },
    { field: 'description', headerName: 'Description', width: 150 },
    { field: 'topic', headerName: 'Topic', width: 120 },
    { field: 'topic_number', headerName: 'Topic #', width: 90 },
    { field: 'q_number', headerName: 'Q #', width: 70 },
    { field: 'condition', headerName: 'Condition', width: 250 },
    { field: 'template', headerName: 'Template', width: 250 },
    { field: 'order_place', headerName: 'Order', width: 80 },
    { field: 'active', headerName: 'Active', width: 80, renderCell: (params) => params.value ? 'Yes' : 'No' }
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button variant="contained" onClick={() => setOpenNewDialog(true)}>
          Add Item Line
        </Button>
        <Button variant="contained" color="secondary" onClick={handleOpenEditDialog} disabled={!selectedRow}>
          Edit Item Line
        </Button>
        <Button variant="outlined" color="error" onClick={handleDelete} disabled={!selectedRow}>
          Delete Item Line
        </Button>
        <Button variant="outlined" onClick={handleMoveUp} disabled={!selectedRow}>
          ↑ Move Up
        </Button>
        <Button variant="outlined" onClick={handleMoveDown} disabled={!selectedRow}>
          ↓ Move Down
        </Button>
      </Stack>

      {loading ? <CircularProgress /> : (
        <DataGrid
          autoHeight
          rowHeight={30}
          rows={rows.filter((r) => canSeeScope(r.product_type))}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50]}
          onRowClick={(params) => setSelectedRow(params.row)}
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
          initialState={{
            sorting: {
              sortModel: [{ field: 'order_place', sort: 'asc' }],
            },
          }}
        />
      )}

      <NewItemLineDialog
        show={openNewDialog}
        onClose={handleNewClose}
      />

      <EditItemLineDialog
        show={openEditDialog}
        data={editRowData}
        onClose={handleEditClose}
        onSave={handleEditSave}
      />
    </Box>
  );
};

export default ItemLinesTab;
